const RECENT_KEY = 'preformiq_form_lift_recent';

export type LiftDef = { id: string; label: string; category: string };

export const LIFT_AUTO: LiftDef = { id: 'auto', label: 'Auto detect', category: 'Auto' };

/** Popular picks shown when not searching (must match catalog names when possible). */
export const COMMON_LIFT_NAMES = [
  'Barbell bench press',
  'Barbell back squat',
  'Conventional deadlift',
  'Overhead press',
  'Barbell row',
  'Pull-up',
  'Lat pulldown',
  'Machine chest press',
  'Leg press',
  'Romanian deadlift',
  'Bulgarian split squat',
  'Incline bench press',
] as const;

export const CATEGORY_ORDER = [
  'Chest',
  'Back',
  'Legs',
  'Shoulders',
  'Arms',
  'Core',
  'Mobility',
  'Other',
] as const;

const CATEGORIES: { label: (typeof CATEGORY_ORDER)[number]; keywords: string[] }[] = [
  { label: 'Chest', keywords: ['bench', 'chest', 'fly', 'push-up', 'pushup', 'dip', 'pec'] },
  { label: 'Back', keywords: ['row', 'pull', 'lat', 'deadlift', 'pulldown', 'chin', 'back lever'] },
  { label: 'Legs', keywords: ['squat', 'leg', 'lunge', 'calf', 'hamstring', 'quad', 'hip thrust', 'glute'] },
  { label: 'Shoulders', keywords: ['shoulder', 'ohp', 'overhead press', 'lateral', 'raise', 'shrug', 'arnold'] },
  { label: 'Arms', keywords: ['curl', 'tricep', 'bicep', 'extension', 'skull', 'forearm', 'wrist'] },
  { label: 'Core', keywords: ['plank', 'crunch', 'ab', 'core', 'twist', 'hollow', 'sit-up', 'situp', 'leg raise'] },
  {
    label: 'Mobility',
    keywords: [
      'stretch',
      'mobility',
      'foam',
      'circle',
      'rotation',
      'band',
      'warmup',
      'warm-up',
      'ankle',
      'hip flexor',
      '90-90',
      'pigeon',
      'cat cow',
      'child pose',
      'yoga',
      'pilates',
    ],
  },
];

export function categorizeLift(name: string): (typeof CATEGORY_ORDER)[number] {
  const lower = name.toLowerCase();
  for (const c of CATEGORIES) {
    if (c.keywords.some((k) => lower.includes(k))) return c.label;
  }
  return 'Other';
}

/** Build a LiftDef from a catalog exercise name (id = label for API payload). */
export function liftFromName(name: string): LiftDef {
  const label = name.trim();
  return { id: label, label, category: categorizeLift(label) };
}

export function liftLabel(idOrName: string): string {
  if (idOrName === 'auto') return LIFT_AUTO.label;
  return idOrName.replace(/_/g, ' ');
}

/** What to send as `lift` to the form-video API. */
export function liftHintForUpload(lift: LiftDef): string {
  return lift.id === 'auto' ? 'auto' : lift.label;
}

export function loadRecentLifts(userId?: string): string[] {
  try {
    const raw = localStorage.getItem(userId ? `${RECENT_KEY}:${userId}` : RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string' && x !== 'auto') : [];
  } catch {
    return [];
  }
}

export function pushRecentLift(userId: string | undefined, liftIdOrLabel: string) {
  if (liftIdOrLabel === 'auto') return;
  const prev = loadRecentLifts(userId).filter(
    (id) => id.toLowerCase() !== liftIdOrLabel.toLowerCase(),
  );
  const next = [liftIdOrLabel, ...prev].slice(0, 8);
  localStorage.setItem(userId ? `${RECENT_KEY}:${userId}` : RECENT_KEY, JSON.stringify(next));
}

export function fuzzyMatchNames(query: string, names: string[]): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return names;
  const words = q.split(/\s+/).filter(Boolean);
  return names.filter((n) => {
    const hay = n.toLowerCase();
    return hay.includes(q) || words.every((w) => hay.includes(w));
  });
}
